/**
 * SC-209: the words and the on/off/auto reading of the mic and camera buttons, in one place.
 *
 * The picture-in-picture window renders its own toolbar — it is a separate document, so it cannot reuse the
 * MUI buttons from the main one — and a floating window whose mic button says something different from the
 * bar behind it reads as two different mics. Pure, so both can call it and a test can pin the wording.
 */

import type { PipMode } from '../LiveKitVideoDisplay/pip/pipModel'
import type { PictureInPictureSurface } from '../LiveKitVideoDisplay/types/LiveKitVideoDisplay.types'

export type MicVisualState = 'live' | 'muted' | 'auto-muted'

export interface MicControlState {
  audioEnabled: boolean
  isAutoMuted: boolean
  autoMuteReason: string
}

/** Drives the icon and the colour: automatically muted is not the same as a mute you chose. */
export function micVisualState(state: { audioEnabled: boolean; isAutoMuted: boolean }): MicVisualState {
  if (state.isAutoMuted) return 'auto-muted'
  if (!state.audioEnabled) return 'muted'
  return 'live'
}

/**
 * The tooltip and the accessible name — the same string, because the button is an icon with no text.
 *
 * An automatic mute explains itself with whatever reason was recorded, but the reason can be empty (the
 * state is reset by clearing it), and an empty tooltip renders as no tooltip at all: a muted mic with no
 * explanation anywhere. The generic line is the floor.
 */
export function micControlLabel(state: MicControlState): string {
  if (state.isAutoMuted) return state.autoMuteReason || 'Microphone muted automatically'
  return state.audioEnabled ? 'Mute microphone' : 'Unmute microphone'
}

export interface CameraControlState {
  videoEnabled: boolean
  /**
   * SC-158 (F8): on, but not shown - held while its background gets ready, or kept off by a problem with
   * it. Only meaningful with `videoEnabled`.
   */
  waitingForBackground?: boolean
  /** SC-158: something is wrong with the chosen background, whatever the cause. */
  backgroundProblem?: boolean
  /** SC-158: the camera pipeline is being rebuilt, so turning the camera on has to wait. */
  busy?: boolean
  /**
   * Camera access looks blocked in the browser, confirmed or only reported by a failed start. Only says so
   * while the camera is off: a live camera can always be turned off.
   */
  blocked?: boolean
}

/**
 * A disabled button says why it is disabled, blocked first because no wait will fix it. Only "on" is ever
 * disabled, so both reasons show only while the camera is off; on a live camera the words say what a click
 * does. A waiting camera is not "on": others do not see you, and the words say so rather than let the user
 * believe otherwise.
 */
export function cameraControlLabel(state: CameraControlState): string {
  if (!state.videoEnabled) {
    if (state.blocked) return 'Camera blocked - allow access in your browser settings'
    if (state.busy) return 'Applying background effect…'
  }
  if (state.videoEnabled && state.waitingForBackground) {
    return state.backgroundProblem
      ? 'Camera on, waiting for background: it can’t be shown yet, so others don’t see you. Turn off camera'
      : 'Camera on, waiting for background: others will see you once it’s ready. Turn off camera'
  }
  return state.videoEnabled ? 'Turn off camera' : 'Turn on camera'
}

/**
 * Only turning the camera on is ever disabled. The connector takes "off" at any time and never delays it,
 * and a button that cannot stop a live camera strands the user with it on. "On" waits while the pipeline
 * rebuilds (WebGL context churn), and stops only for a block the browser itself confirms: a block known
 * only from a failed start may be stale, so a click retries it.
 */
export function cameraControlDisabled(state: {
  videoEnabled: boolean
  busy?: boolean
  blockedConfirmed?: boolean
}): boolean {
  return !state.videoEnabled && Boolean(state.busy || state.blockedConfirmed)
}

export function screenShareControlLabel(state: { screenShareEnabled: boolean }): string {
  return state.screenShareEnabled ? 'Stop sharing screen' : 'Share screen'
}

export function raiseHandControlLabel(state: { raised: boolean }): string {
  return state.raised ? 'Lower hand' : 'Raise hand'
}

/**
 * The pop-out button lives in three places — the call bar, the full-screen share view and the whiteboard
 * view — because the two full-screen views cover the call bar. One button saying three things would read
 * as three features.
 *
 * Safari gets a different promise because it can only keep one: the single-video API has no chrome, so the
 * window holds one share or one face and nothing else. Saying "pop out video" there and delivering one face
 * reads as a broken feature rather than a reduced one. The words stop short of naming Safari because only
 * Safari ever sees them.
 *
 * `!isOpen && !canOpen` is Safari with nothing attached: the attach has to happen before the gesture, so a
 * click cannot fetch something to show. The label says what would make it work; an open window can always
 * be closed, so the open case wins.
 */
export function pipControlLabel(state: {
  mode: PipMode
  isOpen: boolean
  canOpen: boolean
}): string {
  if (state.isOpen) return 'Return video to tab'
  if (!state.canOpen) return 'Nothing to pop out yet - turn on a camera or start a screen share'
  return state.mode === 'video' ? 'Pop out video (shows the share or the speaker)' : 'Pop out video'
}

export interface PipButtonState {
  show: boolean
  /** Drawn disabled with `label` as the reason. Only ever true in the single-video mode. */
  blocked: boolean
  label: string
  /** What a click does: close an open window, otherwise open one as the user. */
  toggle: () => void
}

/**
 * Everything the three pop-out buttons decide, so the call bar and the two full-screen views cannot drift
 * apart. Each keeps its own markup, test ids and colours.
 *
 * `mode === 'none'` (or no control at all) covers both "this browser cannot" and "this office has it
 * switched off". `documentOnly` is the whiteboard view: Safari's single-video window would fall through to
 * a face, so offering it there would promise the wrong thing. `blocked` is Safari with nothing attached
 * (`!isOpen && !canOpen`); a document window can always open, so it is never blocked.
 */
export function pipButtonState(
  pip: PictureInPictureSurface | null | undefined,
  options: { documentOnly?: boolean } = {}
): PipButtonState {
  const show =
    Boolean(pip) && (options.documentOnly ? pip.mode === 'document' : pip.mode !== 'none')
  if (!show) return { show: false, blocked: false, label: '', toggle: () => {} }
  return {
    show,
    blocked: !pip.isOpen && !pip.canOpen,
    label: pipControlLabel(pip),
    toggle: () => (pip.isOpen ? pip.close() : pip.open('user')),
  }
}
